import type { Folder } from "../../features/workspace/sidebar/SideBarTypes";

// Immutable helpers for patching FlashRoom's recursive, embedded folder tree.
// Mirrors the server's recursive tree walkers in server/src/controllers/FolderHelper.ts,
// but returns new arrays/objects instead of mutating in place, since this tree lives in
// React state.

export function findFolderInTree(folders: Folder[], id: string): Folder | null {
    for (const folder of folders) {
        if (folder._id === id) return folder;
        if (folder.folders?.length) {
            const found = findFolderInTree(folder.folders, id);
            if (found) return found;
        }
    }
    return null;
}

export function flattenFolderTree(folders: Folder[]): Folder[] {
    const result: Folder[] = [];

    const walk = (items: Folder[]) => {
        for (const folder of items) {
            result.push(folder);
            walk(folder.folders ?? []);
        }
    };

    walk(folders);
    return result;
}

// Flattens the tree depth-first for a "move to folder" picker, pairing each
// folder with its indent level. Passing excludeSubtreeId skips that folder
// and its whole subtree, so a folder can't be moved into itself or a
// descendant.
export function flattenFolderTreeWithDepth(
    folders: Folder[],
    excludeSubtreeId?: string | null,
    depth = 0
): { folder: Folder; depth: number }[] {
    const result: { folder: Folder; depth: number }[] = [];

    for (const folder of folders) {
        if (excludeSubtreeId && folder._id === excludeSubtreeId) continue;

        result.push({ folder, depth });
        if (folder.folders?.length) {
            result.push(...flattenFolderTreeWithDepth(folder.folders, excludeSubtreeId, depth + 1));
        }
    }

    return result;
}

export function getFolderPath(folders: Folder[], id: string | null): Folder[] {
    if (!id) return [];

    const allFolders = flattenFolderTree(folders);
    const byId = new Map(allFolders.map(folder => [folder._id, folder]));
    const path: Folder[] = [];
    let current = byId.get(id) ?? null;

    while (current) {
        path.unshift(current);
        current = current.folderParent ? byId.get(current.folderParent) ?? null : null;
    }

    return path;
}

export function countFolderContents(folder: Folder): number {
    const childFolders = folder.folders ?? [];

    return (
        (folder.snippets?.length ?? 0) +
        (folder.fileRefs?.length ?? 0) +
        childFolders.length +
        childFolders.reduce((total, child) => total + countFolderContents(child), 0)
    );
}

export function insertFolderNode(
    folders: Folder[],
    parentId: string | null,
    node: Folder
): Folder[] {
    // Dedup: if this id is already present anywhere in the tree, leave the tree untouched.
    if (findFolderInTree(folders, node._id)) return folders;

    if (parentId === null) {
        return [...folders, node];
    }

    let changed = false;

    const next = folders.map((folder) => {
        if (folder._id === parentId) {
            changed = true;
            return { ...folder, folders: [...(folder.folders ?? []), node] };
        }

        if (folder.folders?.length) {
            const nextChildren = insertFolderNode(folder.folders, parentId, node);
            if (nextChildren !== folder.folders) {
                changed = true;
                return { ...folder, folders: nextChildren };
            }
        }

        return folder;
    });

    return changed ? next : folders;
}

export function addIdToFolderArray(
    folders: Folder[],
    folderId: string,
    field: "snippets" | "fileRefs",
    id: string
): Folder[] {
    let changed = false;

    const next = folders.map((folder) => {
        if (folder._id === folderId) {
            const current = folder[field] ?? [];
            if (current.includes(id)) return folder;

            changed = true;
            return { ...folder, [field]: [...current, id] };
        }

        if (folder.folders?.length) {
            const nextChildren = addIdToFolderArray(folder.folders, folderId, field, id);
            if (nextChildren !== folder.folders) {
                changed = true;
                return { ...folder, folders: nextChildren };
            }
        }

        return folder;
    });

    return changed ? next : folders;
}

export function removeIdFromFolderArrays(
    folders: Folder[],
    field: "snippets" | "fileRefs",
    id: string
): Folder[] {
    let changed = false;

    const next = folders.map((folder) => {
        let nextFolder = folder;

        const current = folder[field];
        if (current?.includes(id)) {
            nextFolder = { ...nextFolder, [field]: current.filter((x) => x !== id) };
            changed = true;
        }

        if (nextFolder.folders?.length) {
            const nextChildren = removeIdFromFolderArrays(nextFolder.folders, field, id);
            if (nextChildren !== nextFolder.folders) {
                nextFolder = { ...nextFolder, folders: nextChildren };
                changed = true;
            }
        }

        return nextFolder;
    });

    return changed ? next : folders;
}

// Removes a folder node (and, by omission, its whole subtree) from wherever it
// lives in the tree, returning the pruned tree plus the extracted node.
export function extractFolderNode(
    folders: Folder[],
    id: string
): { tree: Folder[]; node: Folder | null } {
    for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];

        if (folder._id === id) {
            const tree = [...folders.slice(0, i), ...folders.slice(i + 1)];
            return { tree, node: folder };
        }

        if (folder.folders?.length) {
            const result = extractFolderNode(folder.folders, id);
            if (result.node) {
                const tree = [...folders];
                tree[i] = { ...folder, folders: result.tree };
                return { tree, node: result.node };
            }
        }
    }

    return { tree: folders, node: null };
}

// Batch-remove every folder whose id is in `ids` (and, by omission, its subtree)
// in a single pass — used for recursive folder deletes where the server already
// computed the full descendant id list.
export function removeFolderIds(folders: Folder[], ids: Set<string>): Folder[] {
    let changed = false;

    const next: Folder[] = [];
    for (const folder of folders) {
        if (ids.has(folder._id)) {
            changed = true;
            continue;
        }

        if (folder.folders?.length) {
            const nextChildren = removeFolderIds(folder.folders, ids);
            if (nextChildren !== folder.folders) {
                changed = true;
                next.push({ ...folder, folders: nextChildren });
                continue;
            }
        }

        next.push(folder);
    }

    return changed ? next : folders;
}

export function patchFolderInTree(
    folders: Folder[],
    id: string,
    fields: Partial<Folder>
): Folder[] {
    let changed = false;

    const next = folders.map((folder) => {
        if (folder._id === id) {
            changed = true;
            return { ...folder, ...fields };
        }

        if (folder.folders?.length) {
            const nextChildren = patchFolderInTree(folder.folders, id, fields);
            if (nextChildren !== folder.folders) {
                changed = true;
                return { ...folder, folders: nextChildren };
            }
        }

        return folder;
    });

    return changed ? next : folders;
}
